import React, { useState, useEffect } from "react";
import { Eye, Edit3, Trash, ChevronDown, ChevronUp } from "lucide-react";
import AddEditLeadModal from "../components/AddEditLeadModal";
import AddEditActivityModal from "../../Activities/pages/AddEditActivityModal";
import LeadDetailsCanvas from "../components/LeadDetailsCanvas";
import OpportunityForm from "../../Opportunities/components/OpportunityForm";
import { calculatePriority } from "../utils/priorityCalculator";
import { fetchLeads, createLead, updateLead, deleteLead } from "../api/leadService";
import { fetchActivitiesByLead, createActivity, updateActivity, deleteActivity } from "../../Activities/api/activityService";
import { createOpportunity } from "../api/opportunityService";
import { fetchLeadStatuses } from "../api/statusService";
import { calculateProgress } from "../utils/progressCalculator";
import UploadLeadModal from "../components/UploadLeadModal";
import { sortData } from "../utils/sortUtils";
import { fetchBranches } from "../api/branchService"; 
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import DataTable from '../components/DataTable';
import Footer from "../../../components/Footer";

function LeadManagementPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [expandedLeadId, setExpandedLeadId] = useState(null);
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [isEditingActivity, setIsEditingActivity] = useState(false);
  const [isOpportunityFormOpen, setIsOpportunityFormOpen] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadedLeads, setUploadedLeads] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [branchOptions, setBranchOptions] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [sourceFilter, setSourceFilter] = useState([]);
  const [branchFilter, setBranchFilter] = useState([]);
  
    useEffect(() => {
    const loadLeadsAndStatuses = async () => {
      try {
        const [leadsData, statusesData, branchesData] = await Promise.all([
          fetchLeads(), // Fetch leads
          fetchLeadStatuses(), // Fetch statuses from API
          fetchBranches()
        ]);
  
        setLeads(leadsData);
  
        // ✅ Use fetched statuses instead of extracting them from leads
        setStatusOptions(statusesData.map(status => status.name)); 
  
        // Update branch options from API response
        setBranchOptions(branchesData.map(branch => branch.name));

        // ✅ Extract unique sources from leads
        const uniqueSources = [...new Set(leadsData.map(lead => lead.source))];
        setSourceOptions(uniqueSources);
      } catch (error) {
        console.error("❌ Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
  
    loadLeadsAndStatuses();
  }, []);

  const loadActivities = async (leadId) => {
    try {
      const activities = await fetchActivitiesByLead(leadId);
      console.log('Activities:',activities);
      setLeads(prevLeads =>
        prevLeads.map(lead => 
          lead.lead_id === leadId ? { ...lead, activities: activities || [] } : lead
        )
      );
    } catch (error) {
      console.error("❌ Error fetching activities:", error);
    }
  };

  const handleAddActivity = (lead) => {
    setSelectedLead(lead);
    setSelectedActivity(null);
    setIsEditingActivity(false);
    setIsActivityModalOpen(true);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleSaveLead = async (lead) => {
    try {
      const calculatedPriority = calculatePriority(lead);
      const leadWithPriority = { ...lead, priority: lead.priority || calculatedPriority };

      if (isEditing) {
        const updatedLead = await updateLead(lead.lead_id, leadWithPriority);
        setLeads(leads.map(l => l.lead_id === lead.lead_id ? updatedLead : l));
      } else {
        const newLead = await createLead(leadWithPriority);
        setLeads([...leads, { ...newLead, activities: [], opportunities: [] }]);
      }
      setIsModalOpen(false);
      setSelectedLead(null);
    } catch (error) {
      console.error("❌ Error saving lead:", error);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    try {
      await deleteLead(leadId);
      setLeads(leads.filter(lead => lead.lead_id !== leadId));
    } catch (error) {
      console.error("❌ Error deleting lead:", error);
    }
  };

  const handleSaveActivity = async (leadId, activity) => {
    try {
      if (isEditingActivity) {
        await updateActivity(activity.activity_id, { ...activity, lead_id: leadId });
      } else {
        await createActivity({ ...activity, lead_id: leadId });
      }
  
      // ✅ Fetch updated activities
      const updatedActivities = await fetchActivitiesByLead(leadId);
  
      // ✅ Update leads state with new activities
      setLeads(prevLeads =>
        prevLeads.map(lead =>
          lead.lead_id === leadId ? { ...lead, activities: updatedActivities || [] } : lead
        )
      );
  
      setIsActivityModalOpen(false);
      setSelectedActivity(null);
    } catch (error) {
      console.error("❌ Error saving activity:", error);
    }
  };

  

  const handleDeleteActivity = async (leadId, activityId) => {
    try {
      await deleteActivity(activityId);
      await loadActivities(leadId); // ✅ Reload activities after deletion
    } catch (error) {
      console.error("❌ Error deleting activity:", error);
    }
  };

  const handleOpenOpportunityForm = (lead) => {
    setSelectedLead(lead);
    setIsOpportunityFormOpen(true);
  };

  const handleCloseOpportunityForm = () => {
    setIsOpportunityFormOpen(false);
    setSelectedLead(null);
  };

  const handleSaveOpportunity = async (opportunity) => {
    try {
      const newOpportunity = await createOpportunity({
        ...opportunity,
        lead_id: selectedLead.lead_id
      });
      
      const updatedLead = await updateLead(selectedLead.lead_id, {
        ...selectedLead,
        status: "Opportunity Created",
        opportunities: [...(selectedLead.opportunities || []), newOpportunity.id]
      });
      
      setLeads(leads.map(lead => 
        lead.lead_id === selectedLead.lead_id ? updatedLead : lead
      ));
      handleCloseOpportunityForm();
    } catch (error) {
      console.error("❌ Error creating opportunity:", error);
    }
  };

  const handleSaveLeadSuccess = (newLead) => {
    if (isEditing) {
    setLeads(leads.map(l => l.lead_id === newLead.lead_id ? newLead : l));
    } else {
    setLeads(prev => [...prev, newLead]);
    }
  };

  const toggleExpand = async (leadId) => {
    setExpandedLeadId(prevId => (prevId === leadId ? null : leadId));
  
    // ✅ Load activities only if expanding the lead
    if (expandedLeadId !== leadId) {
      await loadActivities(leadId);
    }
  };

  const handleViewDetails = (lead) => {
    setSelectedLead(lead);
    setIsDetailsOpen(true);
  };

  const filteredLeads = leads.filter(lead =>
    (`${lead.first_name} ${lead.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.phone.includes(searchQuery) ||
      lead.organization?.toLowerCase().includes(searchQuery.toLowerCase())) &&
    (statusFilter.length === 0 || statusFilter.some(filter => filter.value === lead.status)) &&
    (sourceFilter.length === 0 || sourceFilter.some(filter => filter.value === lead.source)) &&
    (branchFilter.length === 0 || branchFilter.some(filter => filter.value === lead.branch))
  );

  // Fixed sortedLeads with ID mapping
  const sortedLeads = sortData(filteredLeads, sortConfig).map(lead => ({
    ...lead,
    id: lead.lead_id
  }));
  const animatedComponents = makeAnimated();
  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '44px',
      borderColor: '#e5e7eb',
      borderRadius: '8px',
      zIndex: 20,
      '&:hover': { borderColor: '#93c5fd' },
      '&:focus-within': { borderColor: '#3b82f6', boxShadow: '0 0 0 1px #3b82f6' }
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#e0f2fe',
      borderRadius: '6px',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#0369a1',
      fontWeight: '500',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#0369a1',
      ':hover': { backgroundColor: '#bae6fd', color: '#075985' }
    }),
    menu: (provided) => ({
      ...provided,
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    })
  };
  return (
    <div className="relative flex flex-col h-screen overflow-hidden">
      <div className="flex flex-col md:flex-row justify-between items-center mb-4">
  <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-2 md:mb-0">
    Lead Management
  </h2>
  <div className="flex flex-wrap gap-2">
    <button
      className="bg-blue-600 text-white px-3 py-2 text-sm rounded-md hover:bg-blue-500"
      onClick={() => {
        setIsModalOpen(true);
        setIsEditing(false);
        setSelectedLead(null);
      }}
    >
      + Add Lead
    </button>
    <button
      className="bg-blue-600 text-white px-3 py-2 text-sm rounded-md hover:bg-blue-500"
      onClick={() => setIsUploadModalOpen(true)}
    >
      📤 Upload
    </button>
  </div>
</div>

<div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8 relative z-20">
  <input
    type="text"
    placeholder="🔍 Search Leads..."
    value={searchQuery}
    onChange={(e) => setSearchQuery(e.target.value)}
    className="border p-2 rounded-md w-full"
  />
 {/* Status Filter */}
 <Select
    isMulti
    options={statusOptions.map(status => ({ value: status, label: status }))}
    value={statusFilter}
    onChange={setStatusFilter}
    components={animatedComponents}
    placeholder="Filter by Status"
    styles={customStyles}
    closeMenuOnSelect={false}
    hideSelectedOptions={false}
    noOptionsMessage={() => "No statuses found"}
    className="react-select-container"
    classNamePrefix="react-select"
  />

  {/* Source Filter */}
  <Select
    isMulti
    options={sourceOptions.map(source => ({ value: source, label: source }))}
    value={sourceFilter}
    onChange={setSourceFilter}
    components={animatedComponents}
    placeholder="Filter by Source"
    styles={customStyles}
    closeMenuOnSelect={false}
    noOptionsMessage={() => "No sources found"}
    className="react-select-container"
    classNamePrefix="react-select"
  />

  {/* Branch Filter */}
  <Select
    isMulti
    options={branchOptions.map(branch => ({ value: branch, label: branch }))}
    value={branchFilter}
    onChange={setBranchFilter}
    components={animatedComponents}
    placeholder="Filter by Branch"
    styles={customStyles}
    closeMenuOnSelect={false}
    noOptionsMessage={() => "No branches found"}
    className="react-select-container"
    classNamePrefix="react-select"
  />
</div>

      <DataTable
        columns={[
          {
      key: 'name',
      header: 'Name',
      sortable: false,
      render: (row) => `${row.first_name} ${row.last_name}`
    },
    {
      key: 'phone',
      header: 'Phone',
      sortable: false
    },
    {
      key: 'branch',
      header: 'Branch',
      sortable: true
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true
    },
    {
      key: 'source',
      header: 'Source',
      sortable: true
    },
    {
      key: 'priority',
      header: 'Priority',
      sortable: true,
      render: (row) => (
        <span className={`px-2 py-1 text-xs font-semibold rounded-lg ${
          row.priority === 'High' ? 'bg-red-200 text-red-700' :
          row.priority === 'Medium' ? 'bg-yellow-200 text-yellow-700' :
          'bg-green-200 text-green-700'
        }`}>
          {row.priority}
        </span>
      )
    },
	{
      key: 'progress',
      header: 'Progress',
      sortable: false,
      render: (row) => (
        <div className="relative w-24 h-3 bg-gray-200 rounded-full">
          <div
            className="h-full rounded-full"
            style={{
              width: `${calculateProgress(row.status, row.activities, leadStatuses)}%`,
              backgroundColor: leadStatuses.find(s => s.name === row.status)?.color || "#FF5722",
            }}
          />
        </div>
      )
    },
          {
            key: 'actions',
            header: 'Actions',
            sortable: false,
            render: (row) => (
              <div className="flex items-center justify-center space-x-1">
                <button 
                  onClick={() => handleViewDetails(row)} 
                  className="text-blue-600 hover:text-blue-800 p-1"
                >
                  <Eye size={14} />
                </button>
                <button 
                  onClick={() => { 
                    setIsEditing(true); 
                    setSelectedLead(row); 
                    setIsModalOpen(true);
                  }} 
                  className="text-green-600 hover:text-green-800 p-1"
                >
                  <Edit3 size={14} />
                </button>
                <button 
                  onClick={() => handleDeleteLead(row.id)}
                  className="text-red-600 hover:text-red-800 p-1"
                >
                  <Trash size={14} />
                </button>
                <button 
                  onClick={() => toggleExpand(row.id)}
                  className="text-gray-600 hover:text-gray-800 p-1"
                >
                  {expandedLeadId === row.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
              </div>
            )
          }
        ]}
        data={sortedLeads}
        sortConfig={sortConfig}
        onSort={handleSort}
        renderExpandedRow={(row) => (
          <div className="space-y-2 p-4">
            {row.activities?.length > 0 ? (
              <div className="bg-gray-50 p-4 rounded-lg shadow-md">
                <h3 className="text-md font-semibold text-gray-700 mb-2">Activities:</h3>
                <table className="min-w-full bg-white border border-gray-200 rounded-md">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Type</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Summary</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Date</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Status</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {row.activities.map(activity => (
                      <tr key={activity.activity_id} className="border-t border-gray-200">
                        <td className="px-4 py-2 text-sm text-gray-800">
                          {activity.ActivityType?.name || "Unknown"}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600">
                          {activity.description || "No Description"}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500">
                          {activity.scheduled_at ? new Date(activity.scheduled_at).toLocaleDateString() : "No Date"}
                        </td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-md ${
                            activity.ActivityStatus?.name === "Completed" 
                            ? "bg-green-200 text-green-700" 
                            : "bg-yellow-200 text-yellow-700"
                          }`}>
                            {activity.ActivityStatus?.name || "Unknown"}
                          </span>
                        </td>
                        <td className="px-4 py-2 flex space-x-2">
                          <button
                            onClick={() => {
                              setSelectedActivity(activity);
                              setIsEditingActivity(true);
                              setIsActivityModalOpen(true);
                            }}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Edit3 size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteActivity(row.id, activity.activity_id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500">No activities found.</p>
            )}
            <button
              onClick={() => { 
                setSelectedLead(row);
                setIsActivityModalOpen(true);
                setIsEditingActivity(false);
              }}
              className="text-blue-600 hover:underline mt-2"
            >
              Add Activity
            </button>
            <button
              onClick={() => handleOpenOpportunityForm(row)}
              className="text-green-600 hover:underline ml-4"
            >
              Create Opportunity
            </button>
            {row.opportunities?.length > 0 && (
              <div className="mt-2">
                <p className="text-gray-800 font-medium">Opportunities:</p>
                <ul>
                  {row.opportunities.map(oppId => (
                    <li key={oppId} className="text-gray-600">
                      Opportunity ID: {oppId}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        expandedRowId={expandedLeadId}
      />

      {/* Modals (keep your existing modal implementations) */}
      <AddEditLeadModal
  isOpen={isModalOpen}
  onClose={(newLead) => {
    setIsModalOpen(false);
    setSelectedLead(null);
    if (newLead) {
      // Use functional update for state consistency
      setLeads(prevLeads => {
        if (isEditing) {
          return prevLeads.map(lead => 
            lead.lead_id === newLead.lead_id ? newLead : lead
          );
        }
        return [...prevLeads, newLead];
      });
    }
  }}
  isEditing={isEditing}
  leadId={selectedLead?.lead_id}
/>
      
      {isDetailsOpen && (
        <LeadDetailsCanvas
          lead={selectedLead}
          onClose={() => {
            setSelectedLead(null);
            setIsDetailsOpen(false);
          }}
        />
      )}
      <UploadLeadModal
  isOpen={isUploadModalOpen}
  onClose={() => setIsUploadModalOpen(false)}
  onUploadSuccess={(newLeads) => setUploadedLeads([...leads, ...newLeads])}
/>
      <AddEditActivityModal
        isOpen={isActivityModalOpen}
        onClose={() => setIsActivityModalOpen(false)}
        onSave={(activity) => handleSaveActivity(selectedLead?.lead_id, activity)} // ✅ Pass leadId explicitly
        isEditing={isEditingActivity}
        activity={selectedActivity}
        leadId={selectedLead?.lead_id} // ✅ Ensure leadId is passed
      />

      {isOpportunityFormOpen && (
        <OpportunityForm
          lead={selectedLead}
          onClose={handleCloseOpportunityForm}
          onSave={handleSaveOpportunity}
        />
      )}
      
    </div>
  );
}

export default LeadManagementPage;